import {useSelector, useStore} from 'react-redux';
import {useLocation} from 'react-router';
import {saveEventThunk} from '~/CreateFabricInvitation/api/event';
import {history} from '~/CreateFabricInvitation/history';
import {eventInEditMode} from '~/CreateFabricInvitation/reducers/details/selectors';
import {trackEventId} from '~/CreateFabricInvitation/utils/dataTracking';
import {loginModalPromise} from '~/CreateFabricInvitation/utils/modalPromises';
import {
  webViewOnNav,
  webViewSetBusy,
  webViewSetEventId,
  webViewSetShouldUserLogIn,
} from '~/CreateFabricInvitation/webview/webview';
import {getEventStepsType} from '~/common/fabric/EventGuests/components/DisplayCondition/addGuestsCondition';
import {UPDATE_EVENT_DETAILS} from '~/common/fabric/reducers/details/constants';
import {getErrorMessage} from '~/common/utils/errors';
import {logger} from '~/common/utils/logging';
import {sendUpsellTrack} from '~/common/utils/upsellTracking';
import {isFabricWebview} from '~/common/utils/webview';
import {toastAlert} from '~library/atoms/Toast';
import {isGuamWebviewOrLater} from '~library/organisms/LoginModal/LoginModalWrapper';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {eventSteps} from './createSteps';
import {editSteps} from './editSteps';
import {backToGallery, getLastPathComponent} from './utils';

const toast = (message, toastId) => {
  if (!document.getElementById(toastId)) {
    toastAlert(message, {
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      progress: undefined,
      toastId: toastId || 'navigation-error',
    });
  }
};

const getNavigationSteps = (inEditMode, state) => {
  if (inEditMode) return editSteps;

  const eventType = getEventStepsType(state);

  return eventSteps[eventType];
};

export const getCurrentStep = ({inEditMode, store}) => {
  const navigationSteps = getNavigationSteps(inEditMode, store.getState());
  const {location} = window;
  const lastPathComponent = getLastPathComponent(location.pathname);
  // TODO: add 'aliases' to navigationSteps instead of hard coding it in this function
  const stepName =
    {create: 'customize', design: 'customize'}[lastPathComponent] || lastPathComponent;
  return {...navigationSteps[stepName]};
};

export const useCurrentRoute = () => {
  const inEditMode = useSelector(eventInEditMode);
  const location = useLocation(); // get updated when the path changes
  const store = useStore();
  const navigationSteps = getNavigationSteps(inEditMode, store.getState());
  const currentStep = getCurrentStep({inEditMode, store});

  currentStep.nextRoute =
    typeof currentStep.nextRoute === 'function'
      ? currentStep.nextRoute(store)
      : currentStep.nextRoute;
  currentStep.nextButtonText =
    typeof currentStep.nextButtonText === 'function'
      ? currentStep.nextButtonText(store)
      : currentStep.nextButtonText;
  const nextStep = navigationSteps[currentStep.nextRoute];

  const nextStepAvailable = useSelector(currentStep.nextRouteAvailableSelector || (() => true)); // still have to supply a 'selector' even if current step doesn't have one.
  const nextStepPreEnterRouteStatusMessage = nextStep?.preEnterRouteStatusMessage;

  const previousStep = currentStep.previousRoute;
  const lastPathComponent = getLastPathComponent(location.pathname);

  return {
    ...currentStep,
    path: currentStep.path || lastPathComponent,
    meta: navigationSteps.meta,
    nextRouteAvailableSelector: null,
    nextStepAvailable,
    nextPath: nextStep?.path,
    previousPath: previousStep,
    nextStepPreEnterRouteStatusMessage,
    nextButtonComponent: currentStep.nextButtonComponent
      ? currentStep.nextButtonComponent(store)
      : null,
  };
};

const navigate = (route, useReplace, eventId) => {
  const components = history.location.pathname.split('/');
  components[components.length - 1] = route.path;
  const routeArgs = {
    pathname: components.join('/'),
    hash: history.location.hash,
    search: history.location.search,
  };
  logger(`navigate: navigating to ${route.path} for ${eventId}`, 'info', {
    ...routeArgs,
    useReplace,
  });
  if (useReplace) history.replace(routeArgs);
  else history.push(routeArgs);
};

const getEventIdFromStore = (store) => {
  try {
    return store.getState().eviteEvent.id;
  } catch (e) {
    evite.error('Failed to grab event ID from store', e);
    logger('getEventIdFromStore no event ID found', 'error', {
      message: getErrorMessage(e),
    });
  }
  return '';
};

export const navigateToRoute = async (
  routeName,
  store,
  force = false,
  historyReplace = false,
  extraRouteFunctionArgs = []
) => {
  if (!routeName) {
    return backToGallery();
  }
  let router = routeName;
  const isDyo = store.getState().fabricTemplate.templateName.includes('dyo');
  const {rsvpOptional} = store.getState().fabricTemplate;
  const {inEditMode} = store.getState().details;
  const currentStep = getCurrentStep({inEditMode, store});
  const eventId = getEventIdFromStore(store);
  const isInFabricWebview = isFabricWebview();

  if (router === 'next') {
    if (typeof currentStep.nextRoute === 'function') {
      router = currentStep.nextRoute(store);
    } else {
      router = currentStep.nextRoute;
    }
    logger(`navigateToRoute resolved next route to '${router}' for '${eventId}'`, 'info', {
      routeName,
      isDyo,
      inEditMode,
      currentStep: currentStep?.displayName || 'no currentStep',
      isInFabricWebview,
    });
  }
  if (router === 'back') window.history.back();
  if (!force && currentStep.preLeaveRouteFunction) {
    try {
      const cont = await currentStep.preLeaveRouteFunction(
        store,
        router,
        ...extraRouteFunctionArgs
      );
      if (cont === false)
        /* preLeaveRouteFunction returns false to abort navigation */
        return undefined;
    } catch (e) {
      evite.error('Not navigating to route due to throw from preLeaveRouteFunction', e);
      toast(
        e?.message || 'An error occurred saving your invitation, please contact support',
        'nav-err'
      );
      return undefined;
    }
  }
  if (router === 'previous') {
    if (typeof currentStep.previousRoute === 'function') {
      router = currentStep.previousRoute(store);
    } else {
      router = currentStep.previousRoute;
    }
  }
  if ((!force || force === 'saveAndExit') && currentStep.saveOnLeave) {
    webViewSetBusy(true);
    try {
      const userId = store.getState().user?.userId;
      if (!userId) {
        if (isGuamWebviewOrLater()) {
          webViewSetShouldUserLogIn(true);
          return undefined;
        }
        try {
          await loginModalPromise(store.dispatch);
          // biome-ignore lint/correctness/noUnusedVariables: existing error
        } catch (e) {
          // login window canceled
          return undefined;
        }
      }
      const origEventId = store.getState().eviteEvent.id;
      const {status} = store.getState().eviteEvent;
      const saveToBackend =
        !origEventId ||
        !status ||
        status === 'draft' ||
        currentStep.name === 'review' ||
        (inEditMode && router === 'send');
      // !origEventId --> we're in step 1 before the user has saved any changes
      // status === 'draft' --> we're in the create flow
      // currentStep.name === 'review' --> we're on the Checkout step
      // (inEditMode && router === 'send') --> we're in the edit flow and about to save changes that don't need purchasing
      try {
        await store.dispatch(saveEventThunk(saveToBackend));
      } catch (e) {
        toast(
          e.message || 'An error occurred saving your event, please try again or contact support',
          'nav-err'
        );
        evite.error('Failed to save event', e);
        throw e;
      }
      const newEventId = store.getState().eviteEvent.id;
      sendUpsellTrack(newEventId);
      webViewSetEventId(newEventId);
      if (newEventId !== origEventId) {
        trackEventId(newEventId);
      }
      if (!origEventId && isDyo && rsvpOptional && !inEditMode) {
        store.dispatch({
          type: UPDATE_EVENT_DETAILS,
          // biome-ignore lint/style/useNamingConvention: existing error
          payload: {rsvp_off: false},
        });
      }
    } finally {
      webViewSetBusy(false);
    }
  }
  if (typeof router === 'function') {
    router = router(store);
  }

  const navigationSteps = getNavigationSteps(inEditMode, store.getState());
  const route = navigationSteps[router];
  if (!route) {
    evite.error('Unable to find route', route);
    logger(`navigateToRoute did not find route '${eventId}'`, 'error', {
      route,
      routeName,
      isDyo,
      inEditMode,
      currentStep: currentStep?.displayName || 'no currentStep',
      isInFabricWebview,
    });
    return undefined;
  }
  if (!force && route.preEnterRouteFunction) {
    try {
      webViewSetBusy(true);
      const cont = await route.preEnterRouteFunction(store);
      if (cont === false) {
        /* preEnterRouteFunction returns false to abort navigation */
        logger(
          `navigateToRoute preEnterRouteFunction returned false, not navigating for '${eventId}'`,
          'warn',
          {
            route,
            routeName,
            isDyo,
            inEditMode,
            currentStep: currentStep?.displayName || 'no currentStep',
            isInFabricWebview,
          }
        );
        return undefined;
      }
    } catch (e) {
      evite.error('Not navigating to route due to throw from preEnterRouteFunction', e);
      toast(e?.message || 'An error occurred saving your invitation, please contact support');
      logger(
        `navigateToRoute preEnterRouteFunction threw error, navigation failed for '${eventId}'`,
        'error',
        {
          message: getErrorMessage(e),
          route,
          routeName,
          isDyo,
          inEditMode,
          currentStep: currentStep?.displayName || 'no currentStep',
          isInFabricWebview,
        }
      );
      return undefined;
    } finally {
      webViewSetBusy(false);
    }
  }
  if (isInFabricWebview && route.hideInWebView) {
    // Only notify the app of the new route, don't update our pathname
    const nextButtonText =
      typeof route.nextButtonText === 'function'
        ? route.nextButtonText(store)
        : route.nextButtonText;
    webViewOnNav(router, route.nextRoute, route.previousRoute, nextButtonText);
  } else if (route.path.indexOf('/') < 0) {
    logger(`navigateToRoute navigating for '${eventId}'`, 'info', {
      route,
      routeName,
      isDyo,
      inEditMode,
      currentStep: currentStep?.displayName || 'no currentStep',
      isInFabricWebview,
    });
    navigate(route, historyReplace, eventId);
  } else if (router === 'exit' && currentStep.id === 1 && force !== 'saveAndExit') {
    window.history.back();
  } else {
    window.location = route.path;
  }
  return undefined;
};

export const setupWebviewNavigation = (store) => {
  window.webview = window.webview || {};
  window.navigateToRoute = (route) => {
    navigateToRoute(route, store);
  };
};
